import Trix from "trix";
import ModuleSelectorModal from "./module-selector-modal";

// Handles editing of existing modules in Trix editor
class ModuleEditor {
  constructor(editor, moduleSchemas) {
    this.editor = editor;
    this.moduleSchemas = moduleSchemas;
    this.modal = new ModuleSelectorModal();
    this.installEventHandlers();
  }

  installEventHandlers() {
    // Listen for clicks on module attachments (only marketing page modules)
    this.editor.element.addEventListener("click", (event) => {
      // Check if click is on a module thumbnail/preview
      const moduleElement = event.target.closest(".marketing-page-module");
      if (!moduleElement) return;

      // Find the attachment element
      const attachmentElement = moduleElement.closest("[data-trix-attachment]");
      if (!attachmentElement) return;

      event.preventDefault();
      event.stopPropagation();

      const attachmentId = attachmentElement.dataset.trixAttachmentId;
      if (!attachmentId) return;

      const document = this.editor.getDocument();
      const attachment = document.getAttachmentById(attachmentId);

      if (attachment) {
        const attributes = attachment.getAttributes();
        // Only handle if it has an sgid (ActionText attachable)
        if (attributes.sgid) {
          this.handleModuleClick(attachment);
        }
      }
    });
  }

  handleModuleClick(attachment) {
    const { sgid } = attachment.getAttributes();
    if (!sgid) return;

    // Fetch module data
    this.fetchModule(sgid)
      .then((moduleData) => {
        if (moduleData && moduleData.module_type) {
          const schema = this.moduleSchemas[moduleData.module_type];
          if (schema) {
            // Show edit form with existing configuration
            this.modal.showConfig(
              moduleData.module_type,
              schema,
              (type, config) => {
                this.updateModule(sgid, type, config, attachment);
              },
              () => {
                // User cancelled - do nothing
              },
              moduleData.configuration || {}
            );
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching module:", error);
        alert("Failed to load module for editing.");
      });
  }

  fetchModule(sgid) {
    return fetch(`/cms/marketing_page_modules/${encodeURIComponent(sgid)}`, {
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": document.head.querySelector('meta[name="csrf-token"]')
          .content,
      },
    }).then((response) => {
      if (response.ok) {
        return response.json();
      }
      throw new Error("Failed to fetch module");
    });
  }

  updateModule(sgid, moduleType, configuration, attachment) {
    fetch(`/cms/marketing_page_modules/${encodeURIComponent(sgid)}`, {
      method: "PATCH",
      body: JSON.stringify({
        configuration,
      }),
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": document.head.querySelector('meta[name="csrf-token"]')
          .content,
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error("Failed to update module");
      })
      .then((json) => {
        if (json && json.sgid) {
          // Update the attachment in place
          const newAttachment = new Trix.Attachment({
            sgid: json.sgid,
            content: json.content,
          });

          // Replace the old attachment
          const document = this.editor.getDocument();
          const position = document.getPositionOfAttachment(attachment);
          if (position !== null) {
            document.removeAttachment(attachment);
            document.insertAttachmentAtPosition(newAttachment, position);
            this.editor.loadDocument(document);
          }
        }
      })
      .catch((error) => {
        console.error("Error updating module:", error);
        alert("Failed to update module. Please try again.");
      });
  }
}

export default ModuleEditor;
